import { supabase } from '@/lib/supabase';
import { db } from '@/lib/db';
import { reloadFromCache } from '@/state/data';
import type { Photo, DocumentRow } from '@/types';

/** Photo pipeline: pick/capture -> client-side resize -> Storage upload ->
 *  photos row. Documents additionally get an OCR ingestion row. */

const MAX_DIM = 1600;
const QUALITY = 0.8;

export async function resizeImage(file: File): Promise<{ blob: Blob; width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', QUALITY)
  );
  return { blob, width: w, height: h };
}

export async function uploadPhoto(
  file: File,
  opts: { animalId?: string | null; caption?: string | null; kind?: 'animal' | 'document' }
): Promise<Photo> {
  const { blob, width, height } = await resizeImage(file);
  const id = crypto.randomUUID();
  const day = new Date().toISOString().slice(0, 10);
  const path = `${opts.kind ?? 'animal'}/${day}/${id}.jpg`;

  const { error: upErr } = await supabase.storage.from('photos').upload(path, blob, {
    contentType: 'image/jpeg',
    cacheControl: '31536000',
  });
  if (upErr) throw new Error(upErr.message);

  const photo: Photo = {
    id,
    animal_id: opts.animalId ?? null,
    storage_path: path,
    kind: opts.kind ?? 'animal',
    caption: opts.caption ?? null,
    taken_at: new Date().toISOString(),
    width,
    height,
    created_at: new Date().toISOString(),
  };
  const { error } = await supabase.from('photos').insert(photo);
  if (error) throw new Error(error.message);
  await db.photos.put(photo);
  await reloadFromCache();
  return photo;
}

export async function createDocument(photoId: string): Promise<DocumentRow> {
  const doc: DocumentRow = {
    id: crypto.randomUUID(),
    photo_id: photoId,
    status: 'uploaded',
    raw_text: null,
    extraction: null,
    error: null,
    confirmed_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  const { error } = await supabase.from('documents').insert(doc);
  if (error) throw new Error(error.message);
  await db.documents.put(doc);
  return doc;
}

/** Kick the OCR function and return the updated document. */
export async function ingestDocument(documentId: string): Promise<DocumentRow> {
  const { data: sess } = await supabase.auth.getSession();
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ingest-document`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sess.session?.access_token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ document_id: documentId }),
  });
  if (!res.ok) throw new Error(`ingest failed (${res.status})`);
  const doc = (await res.json()) as DocumentRow;
  await db.documents.put(doc);
  return doc;
}

const urlCache = new Map<string, { url: string; expires: number }>();

export async function photoUrl(storagePath: string): Promise<string | null> {
  const hit = urlCache.get(storagePath);
  if (hit && hit.expires > Date.now()) return hit.url;
  const { data, error } = await supabase.storage.from('photos').createSignedUrl(storagePath, 3600 * 24);
  if (error || !data) return null;
  urlCache.set(storagePath, { url: data.signedUrl, expires: Date.now() + 3600_000 * 23 });
  return data.signedUrl;
}
